<template>
  <tr class="discounts-row">
    <td
      v-for="(value, zoneId) in productTable.discounts"
      :key="'discount-' + zoneId"
      :class="['dark-grey-bckgr', zoneId === 'weight' && 'weight-cell']"
      data-cy="discount-controller"
    >
      <InputDiscountGroup
        v-if="zoneId !== 'weight'"
        :key="zoneId"
        :zone="zoneId"
        :initValue="value"
        :areCurrentDiscountsLocked="areCurrentDiscountsLocked"
        :maxDiscountRate="maxDiscountsRates[zoneId]"
        @onChange="handleChangeDiscount"
      />
      <template v-else>
        <BlockDiscountsDialog
          v-if="isMaxDiscountReached && canEditOffer && !areDiscountsBlocked"
          @onChangeDiscountsLocked="updateAreCurrentDiscountsLocked"
          :areCurrentDiscountsLocked="areCurrentDiscountsLocked"
        />
        <SecondaryButton
          v-if="isMaxDiscountReached && canEditOffer"
          label="RESET"
          dataCy="reset-button"
          @onClick="handleClickResetButton"
        />
        <SecondaryButton
          v-else-if="!isMaxDiscountReached && canEditOffer"
          label="MAX"
          dataCy="max-button"
          @onClick="handleClickMaxButton"
        />
      </template>
    </td>
  </tr>
</template>

<script>
import SecondaryButton from '@/components/buttons/SecondaryButton'
import InputDiscountGroup from '@/containers/offer-form/shipping/InputDiscountGroup'
import BlockDiscountsDialog from '@/containers/offer-form/shipping/BlockDiscountsDialog'
import { mapGetters, mapMutations } from 'vuex'
import {
  GET_PRODUCT_TABLE,
  GET_PRODUCT_MAX_DISCOUNT_RATES,
  GET_PRODUCT_STD_DISCOUNT_RATES,
} from '@/store/modules/offer/getters'
import {
  UPDATE_PRODUCT_DISCOUNT,
  SET_ALL_DISCOUNTS_OF_PRODUCT,
} from '@/store/modules/offer/mutations'

export default {
  name: 'DiscountsRow',
  components: {
    InputDiscountGroup,
    SecondaryButton,
    BlockDiscountsDialog,
  },
  props: {
    id: {
      type: String,
    },
  },
  data() {
    return {
      areCurrentDiscountsLocked: true,
    }
  },
  computed: {
    ...mapGetters('offer', [
      GET_PRODUCT_TABLE,
      GET_PRODUCT_MAX_DISCOUNT_RATES,
      GET_PRODUCT_STD_DISCOUNT_RATES,
      'canEditOffer',
    ]),
    ...mapGetters('rateModel', ['areDiscountsBlocked']),
    productTable() {
      return this[GET_PRODUCT_TABLE](this.id)
    },
    maxDiscountsRates() {
      return this[GET_PRODUCT_MAX_DISCOUNT_RATES](this.id)
    },
    stdDiscountsRates() {
      return this[GET_PRODUCT_STD_DISCOUNT_RATES](this.id)
    },
    isMaxDiscountReached() {
      return Object.keys(this.maxDiscountsRates).some(
        zoneId => this.productTable.discounts[zoneId] >= this.maxDiscountsRates[zoneId],
      )
    },
  },
  methods: {
    ...mapMutations('offer', [UPDATE_PRODUCT_DISCOUNT, SET_ALL_DISCOUNTS_OF_PRODUCT]),
    handleChangeDiscount(zoneId, value) {
      this[UPDATE_PRODUCT_DISCOUNT]({ productId: this.id, zoneId, discount: value })
      !this.areCurrentDiscountsLocked && this.checkMaxDiscountExceeded(zoneId)
      this.$emit('updateDiscounts')
    },
    handleClickResetButton() {
      this.setDiscounts(this.stdDiscountsRates)
    },
    handleClickMaxButton() {
      this.setDiscounts(this.maxDiscountsRates)
    },
    setDiscounts(discountRates) {
      this[SET_ALL_DISCOUNTS_OF_PRODUCT]({
        productId: this.id,
        discountRates,
      })
      this.resetListMaxDiscountExceeded()
      this.$emit('updateDiscounts')
    },
    updateAreCurrentDiscountsLocked() {
      if (!this.areCurrentDiscountsLocked) {
        this.correctDiscountsAboveLimit()
      }
      this.areCurrentDiscountsLocked = !this.areCurrentDiscountsLocked
    },
    correctDiscountsAboveLimit() {
      for (const [zoneId, maxDiscount] of Object.entries(this.maxDiscountsRates)) {
        if (this.productTable.discounts[zoneId] > maxDiscount) {
          this[UPDATE_PRODUCT_DISCOUNT]({ productId: this.id, zoneId, discount: maxDiscount })
        }
      }
    },
    checkMaxDiscountExceeded(zoneId) {
      const isMaxDiscountExceeded =
        this.productTable.discounts[zoneId] > this.maxDiscountsRates[zoneId]
      this.$emit('updateIsMaxDiscountLimitExceeded', zoneId, isMaxDiscountExceeded)
    },
    resetListMaxDiscountExceeded() {
      for (const zoneId of Object.keys(this.maxDiscountsRates)) {
        this.$emit('updateIsMaxDiscountLimitExceeded', zoneId, false)
      }
    },
    initializeListMaxDiscountExceeded() {
      for (const zoneId of Object.keys(this.maxDiscountsRates)) {
        this.checkMaxDiscountExceeded(zoneId)
      }
    },
    initializeAreCurrentDiscountsLocked() {
      const doInitDiscountsExceedMaxLimits = Object.keys(this.maxDiscountsRates).some(
        zoneId => this.productTable.discounts[zoneId] > this.maxDiscountsRates[zoneId],
      )
      this.areCurrentDiscountsLocked = !doInitDiscountsExceedMaxLimits
    },
  },
  mounted() {
    this.initializeListMaxDiscountExceeded()
    this.initializeAreCurrentDiscountsLocked()
  },
}
</script>

<style scoped lang="scss">
.dark-grey-bckgr {
  background-color: $grey-dark;
}
.discounts-row {
  height: 95px;
}
.weight-cell {
  width: 130px;
}
</style>
